// BR-GEN-S1-08: Arabic renders in IBM Plex Sans Arabic — bundled, not fetched
// from a CDN, so the strict CSP stays intact.
import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/700.css';
// ADM-08 §5 field 3 — the three Latin faces the Font style card offers.
//
// VARIABLE fonts, one stylesheet and one file per family, and the reason is
// measurable rather than aesthetic: the first cut of this imported four static
// weights per family — twelve stylesheets — and in dev that is twelve extra
// entries in the module graph before the app module executes. It was enough to
// push first paint past two journeys' un-awaited assertions, which is a real
// cost paid by real first loads and not only by tests.
//
// One variable file per family carries EVERY weight the Font weight card
// offers (300 Light, 400 Regular, 500 Medium) and the 600/700 headings use, so
// this is strictly more coverage for a quarter of the requests. Self-hosted
// like the Arabic face, for the same reason: the CSP has no `font-src`
// exception and must not grow one. The font BINARY is still fetched only when
// a rule matches, so a person on the default downloads one family.
import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource-variable/ibm-plex-sans';
import '@fontsource-variable/source-serif-4';

import { DirectionProvider } from '@radix-ui/react-direction';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router/dom';

import { createAppRouter } from '@/app/router';
import { AmbientBackdrop } from '@/components/ambient-backdrop';
import { i18n } from '@/i18n';
import { initUi, useUiStore } from '@/lib/ui-store';

import './index.css';

// Locale (and with it dir + lang on <html>) and theme, before first paint.
initUi();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // An ERP screen prefers a brief staleness over a surprise refetch storm;
      // per-query overrides opt back in where freshness matters.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createAppRouter();

function Root() {
  // Radix positions popovers/menus off this context; the document's `dir`
  // attribute alone is not read by Radix primitives (§2.6 caveat).
  const locale = useUiStore((s) => s.locale);
  return (
    <DirectionProvider dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          {/* One fixed canvas under every route, sign-in included; see the
              component for why it lives here and not in the shell. */}
          <AmbientBackdrop />
          <RouterProvider router={router} />
        </QueryClientProvider>
      </I18nextProvider>
    </DirectionProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
